import React, { useState } from "react";
import type { ImageFile } from "../App";
import { EditModal } from "./EditModal";
import { uploadToDiscordWithNotification } from "../utils/discord";

interface ImagesProps {
  images: ImageFile[];
  onDelete: (id: number) => void;
  isDarkMode: boolean;
}

export function Images({ images, onDelete, isDarkMode }: ImagesProps) {
  return (
    <div>
      <h2 className={`hidden text-xl font-semibold mb-4 transition-colors duration-300 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
        Gambar: {images.length}
      </h2>
      <div className="gap-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {images.map((image) => {
          if(image.file.type.includes("video")) {
            return <Video video={image} key={image.id} isDarkMode={isDarkMode} />;
          } else {
            return <ImageSpot image={image} onDelete={onDelete} key={image.id} isDarkMode={isDarkMode} />;
          }
        })}
      </div>
    </div>
  );
}

function Video({ video, isDarkMode }: { video: ImageFile; isDarkMode: boolean }) {
  const url = URL.createObjectURL(video.file);
  return (
    <div className={`rounded-lg shadow-md p-3 transition-colors duration-300 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
      <video
        className="rounded-lg aspect-square object-cover"
        loop
        muted
        autoPlay
        src={url}
      ></video>
    </div>
  );
}

interface ImageSpotProps {
  image: ImageFile;
  onDelete: (id: number) => void;
  isDarkMode: boolean;
}

function ImageSpot({ image, onDelete, isDarkMode }: ImageSpotProps) {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [processedImageUrl, setProcessedImageUrl] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState("");


  const url = URL.createObjectURL(image.file);
  const processedURL = image.processedFile ? URL.createObjectURL(image.processedFile) : "";
  const isProcessing = !image.processedFile;

  const handleEditSave = (editedImageUrl: string) => {
    setProcessedImageUrl(editedImageUrl);
  };

  const getImageForEdit = () => {
    if (processedImageUrl) {
      return {
        ...image,
        processedFile: null,
        editedUrl: processedImageUrl
      };
    }
    return image;
  };

  const handleUploadAndDownload = async () => {
    const imageToDownload = processedImageUrl || processedURL;
    const filename = `biji.scz.my.id-${image.id}-${Date.now()}.png`;
    
    if (!imageToDownload) {
      alert('Tidak ada gambar untuk diunduh');
      return;
    }

    setIsUploading(true);
    setUploadStatus('Mempersiapkan upload...');

    try {
      // Upload ke Discord
      const result = await uploadToDiscordWithNotification(
        imageToDownload,
        filename,
        (status) => setUploadStatus(status)
      );

      if (result.success) {
        setUploadStatus('Upload berhasil! Memulai download...');
        
        // Download ke user
        const link = document.createElement('a');
        link.href = imageToDownload;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        setUploadStatus('Download selesai!');
        
        // Reset status setelah 3 detik
        setTimeout(() => {
          setUploadStatus('');
          setIsUploading(false);
        }, 3000);
      } else {
        setUploadStatus(`Upload gagal: ${result.error}`);
        setTimeout(() => {
          setUploadStatus('');
          setIsUploading(false);
        }, 5000);
      }
    } catch (error) {
      console.error('Error during upload and download:', error);
      setUploadStatus('Terjadi kesalahan saat upload');
      setTimeout(() => {
        setUploadStatus('');
        setIsUploading(false);
      }, 5000);
    }
  };

  const transparentBg = isDarkMode 
    ? `url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQBAMAAADt3eJSAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAGUExURTMzM////0EEUjQAAAAJcEhZcwAADsMAAA7DAcdvqGQAAAAUSURBVBjTYwABQSCglEENMxgYGAAynwRB8BEAgQAAAABJRU5ErkJggg==")`
    : `url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQBAMAAADt3eJSAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAGUExURb+/v////5nD/3QAAAAJcEhZcwAADsMAAA7DAcdvqGQAAAAUSURBVBjTYwABQSCglEENMxgYGAAynwRB8BEAgQAAAABJRU5ErkJggg==")`;

  return (
    <div className={`rounded-lg shadow-md overflow-hidden transition-colors duration-300 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
      <div className="relative">
        {isProcessing ? (
          <div className="relative">
            <img
              className="w-full aspect-square object-cover opacity-50 transition-opacity duration-200"
              src={url}
              alt={`Memproses gambar ${image.id}`}
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="bg-black bg-opacity-50 px-4 py-2 rounded-lg">
                <div className="inline-block animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                <span className="text-white font-medium">Memproses...</span>
              </div>
            </div>
          </div>
        ) : (
          <div 
            className="w-full aspect-square"
            style={{ 
              background: transparentBg,
              backgroundRepeat: 'repeat'
            }}
          >
            <img
              className="w-full h-full object-cover transition-opacity duration-200"
              src={processedImageUrl || processedURL}
              alt={`Gambar yang diproses ${image.id}`}
            />
          </div>
        )}
      </div>

      {!isProcessing && (
        <div className={`p-3 border-t transition-colors duration-300 ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
          <div className="flex justify-center gap-2">
            <button
              onClick={() => onDelete(image.id)}
              className={`flex items-center gap-1 px-3 py-1.5 border rounded-md transition-colors duration-300 ${
                isDarkMode 
                  ? 'bg-gray-700 border-gray-600 hover:bg-gray-600 text-gray-200' 
                  : 'bg-white border-gray-200 hover:bg-gray-50 text-gray-700'
              }`}
              title="Hapus"
              disabled={isUploading}
            >
              <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              <span className="text-sm">Hapus</span>
            </button>
            <button
              onClick={() => setIsEditModalOpen(true)}
              className={`flex items-center gap-1 px-3 py-1.5 border rounded-md transition-colors duration-300 ${
                isDarkMode 
                  ? 'bg-gray-700 border-gray-600 hover:bg-gray-600 text-gray-200' 
                  : 'bg-white border-gray-200 hover:bg-gray-50 text-gray-700'
              }`}
              title="Edit"
            disabled={isUploading}
            >
              <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
              <span className="text-sm">Edit</span>
            </button>
            <button
              onClick={handleUploadAndDownload}
              className={`flex items-center gap-1 px-3 py-1.5 border rounded-md transition-colors duration-300 ${
                isUploading
                  ? 'opacity-50 cursor-not-allowed'
                  : isDarkMode 
                  ? 'bg-gray-700 border-gray-600 hover:bg-gray-600 text-gray-200' 
                  : 'bg-white border-gray-200 hover:bg-gray-50 text-gray-700'
              }`}
              title="Unduh"
            disabled={isUploading}
            >
              <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              <span className="text-sm">Unduh</span>
            </button>
          </div>
        </div>
      )}

      <EditModal
        image={getImageForEdit()}
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSave={handleEditSave}
        isDarkMode={isDarkMode}
      />
    </div>
  );
}